import * as z from "zod";
import { Id } from "./ids.js";
import { CommitmentStateSchema } from "./ticket-work.js";

/**
 * jhl-Bots (PIV-48): what a bot is doing right now. `active` while one of its runs works
 * (queued, leased or running), whatever started it; a run that only waits for the user is
 * not active. `since` is when the oldest working run started; `ticketRef` names the ticket
 * whose work session runs.
 */
export const BotActivitySchema = z.object({
  botId: Id,
  active: z.boolean(),
  since: z.string().nullable(),
  ticketRef: z.string().nullable(),
});
/** What a bot is doing right now, as returned by bots.activity. */
export type BotActivity = z.infer<typeof BotActivitySchema>;

/** jhl-Bots (PIV-48): a to-do line's status; RAK-92: removed (closed) tickets never show. */
export const TodoItemStatusSchema = z.enum(["todo", "doing", "review", "blocked", "done"]);
/** Status of a to-do line. */
export type TodoItemStatus = z.infer<typeof TodoItemStatusSchema>;

/**
 * jhl-Bots (PIV-48, RAK-92): one line of a to-do list; `label` and `blockedReason` are plain
 * text, never HTML. `working` while a run of the ticket is queued, leased or running; `state`
 * says where the commitment really stands (Arbeitsmodell).
 * `progress` counts plan steps, null without a plan. `startedAt`, `durationMs`, `costUsd` and
 * `totalTokens` cover the ticket's linked work sessions (runs.ticketId): all null without one,
 * cost and tokens also null when no usage was recorded. `durationMs` sums each started
 * session's end (completion, now while active) minus its start.
 */
export const TodoItemSchema = z.object({
  id: Id,
  ref: z.string(),
  label: z.string(),
  status: TodoItemStatusSchema,
  updatedAt: z.string(),
  ownerBotId: Id.nullable(),
  ownerName: z.string().nullable(),
  working: z.boolean(),
  blockedReason: z.string().nullable(),
  progress: z.object({ done: z.number().int(), total: z.number().int() }).nullable(),
  startedAt: z.string().nullable(),
  durationMs: z.number().int().nullable(),
  costUsd: z.number().nullable(),
  totalTokens: z.number().int().nullable(),
  /**
   * jhl-Bots (Arbeitsmodell): where the commitment really stands (commitmentState). Every server
   * from this version sends it; it is optional only so older servers' answers still parse.
   */
  state: CommitmentStateSchema.optional(),
});
/** One line of a to-do list. */
export type TodoItem = z.infer<typeof TodoItemSchema>;

/**
 * jhl-Bots (Arbeitsmodell, Stufe 2): a request of the person that still needs someone: the bot
 * ended without deciding about it (`unanswered`), asked back (`asked`), or its run ended without
 * completing (`open`). `excerpt` is plain text, never HTML.
 */
export const BotRequestItemSchema = z.object({
  id: Id,
  excerpt: z.string(),
  status: z.enum(["open", "unanswered", "asked"]),
  createdAt: z.string(),
});
/** One request in a to-do list. */
export type BotRequestItem = z.infer<typeof BotRequestItemSchema>;

/**
 * jhl-Bots (PIV-48, RAK-92): a bot's open tickets and its most recently done one, in the order
 * to show: todo (newest first), then doing, review, blocked (last changed first), then done.
 * `team` when the bot oversees other bots: their open tickets are mixed in by the same order.
 */
export const BotTodoOutput = z.object({
  items: z.array(TodoItemSchema),
  team: z.boolean(),
  /** jhl-Bots (Arbeitsmodell): the person's requests to the bot that still need someone. */
  requests: z.array(BotRequestItemSchema).optional(),
});
/** Result of bots.todo. */
export type BotTodoOutput = z.infer<typeof BotTodoOutput>;

/**
 * jhl-Bots (Sitzungen): one work session of a bot, its own chat for one ticket. `working` while a turn
 * of it runs; a closed session (`endReason` done, closed or handover) stays readable for a week. It carries its
 * ticket's purpose and the model and effort it runs on, for the session's header.
 */
export const BotSessionItemSchema = z.object({
  id: Id,
  ref: z.string().nullable(),
  title: z.string(),
  status: z.enum(["open", "closed"]),
  endReason: z.string().nullable(),
  working: z.boolean(),
  /** Stufe 2: how full the session's context was at its latest model call, and where it hands over. */
  contextTokens: z.number().int().nullable(),
  contextLimit: z.number().int().nullable(),
  /** The model the session runs on (its own or the bot's), null when not known yet. */
  model: z.string().nullable(),
  /** The thinking effort it runs with; null is the model's default. */
  effort: z.string().nullable(),
  /** Whether model or effort were chosen for this ticket instead of following the bot. */
  modelChosen: z.boolean(),
  /** What the session is for: its ticket's purpose, done criterion, plan progress and who asked. */
  ticket: z
    .object({
      status: z.string(),
      description: z.string().nullable(),
      doneCriteria: z.string().nullable(),
      planDone: z.number().int(),
      planTotal: z.number().int(),
      /** The bot that asked for the work; null when a person asked. */
      requestedBy: z.string().nullable(),
    })
    .nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
});
/** One session in a bot's session list. */
export type BotSessionItem = z.infer<typeof BotSessionItemSchema>;
